import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from '@mui/material';
import { Link } from "react-router-dom";

import { formatBillingHistoryRange, formatNumberToThousands, formateDateAndTime } from "~/utils/utils";
import { useTrans } from '~/hooks/useTrans';
import { ITransactionApproval } from "~/providers/interfaces";
import Loader from '~/components/Loader';
import { useLang } from "~/hooks/useLang";
import { getLink } from "~/providers/route";

interface IAdminTransactionsTable {
	loading: boolean;
	items: Array<ITransactionApproval>;
	onApprove: (transaction: ITransactionApproval) => void;
}

export default function AdminTransactionsTable({
	loading = true,
	items = [],
	onApprove,
}: IAdminTransactionsTable) {
	const { trans, formatPennyPrice, transProjectBusinessPlan, transBillingMethod, formatCredits, formatPricePerCredit, transApprovalReason } = useTrans();
	const { isCzech } = useLang();
	const HEADER = [
		"adminTransactionsTable.projectId",
		"adminTransactionsTable.requestedAt",
		"adminTransactionsTable.range",
		"projectDashboardPage.program",
		"projectDashboardPage.approvalReason",
		"createProject.billingMethod",
		"projectDashboardPage.consumption.consumed",
		"projectDashboardPage.consumption.freeCredits",
		"projectDashboardPage.thousandCreditsPrice",
		"projectFinancialPage.paidConsumption.monthlyLimit",
		"adminTransactionsTable.creditsCharged",
		"billingHistoryTable.fixedBillingAmount",
		"projectDashboardPage.consumption.amountTotal",
		"",
	];

	return <TableContainer component={Paper}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					{ HEADER.map(header => <TableCell sx={{ fontWeight: "bold" }} key={header}>
						{ header && trans(header) }
					</TableCell>) }
				</TableRow>
			</TableHead>
			<TableBody>
				{ items.map(transaction => (<TableRow key={transaction.billingHistoryId}>
					<TableCell>
						<Link to={getLink("project-dashboard", true, { projectId: transaction.projectId })}>
							{ transaction.projectInternalName }
						</Link>
					</TableCell>
					<TableCell>
						{ formateDateAndTime(new Date(transaction.requestedAt), isCzech) }
					</TableCell>
					<TableCell>
						{ formatBillingHistoryRange(transaction, isCzech) }
					</TableCell>
					<TableCell>
						{ transProjectBusinessPlan(transaction.businessPlanType) }
					</TableCell>
					<TableCell>
						{ transApprovalReason(transaction.approvalReason) }
					</TableCell>
					<TableCell>
						{ transBillingMethod(transaction.billingMethod) }
					</TableCell>
					<TableCell>
						{ formatCredits(transaction.totalConsumedCredits) }
					</TableCell>
					<TableCell>
						{ formatNumberToThousands(transaction.creditsFreeOfCharge) }
					</TableCell>
					<TableCell>
						{ formatPricePerCredit(transaction.pricePerCredit) }
					</TableCell>
					<TableCell>
						{ formatCredits(transaction.totalConsumptionLimit) }
					</TableCell>
					<TableCell>
						{ formatNumberToThousands(transaction.creditsCharged) }
					</TableCell>
					<TableCell>
						{ formatPennyPrice(transaction.fixedBillingAmount) }
					</TableCell>
					<TableCell>
						{ formatPennyPrice(transaction.amountTotal) }
					</TableCell>
					<TableCell className="text-align-center">
						<Button color="success" variant="outlined" onClick={() => onApprove(transaction)}>
							{ trans("approveTransactionModal.title") }
						</Button>
					</TableCell>
				</TableRow>)) }
				{ items.length === 0 && <TableRow>
					<TableCell colSpan={HEADER.length} className="text-align-center">
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
